import React, { useState, useEffect } from 'react';
import { 
  FaArrowLeft, 
  FaCloudUploadAlt, 
  FaSave, 
  FaStar, 
  FaBox, 
  FaAlignLeft, 
  FaTag, 
  FaRupeeSign,
  FaPercentage,
  FaImages
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import SummaryApi from '../common';
import { toast } from 'react-toastify';

const ProductUpdate = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // State
  const [product, setProduct] = useState({
    product_name: '',
    product_description: '',
    price: '',
    product_img1: '',
    product_img2: '',
    product_code: '',
    sub_title: '',
    specifications: '',
    discount: '',
    after_discount_price: '',
    rating: '5',
    is_active: '1'
  });

  // Preview URLs for new file uploads
  const [previews, setPreviews] = useState({
    img1: null,
    img2: null
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await fetch(SummaryApi.product.getById.url, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${SummaryApi.bearerToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ product_id: id })
      });

      const data = await response.json();

      if (!data.status) {
        toast.error(data.message || "Failed to load product");
        navigate('/admin-panel/products');
        return;
      }

      const productData = data.data;

      // Construct Image URLs
      // Note: Adjust SummaryApi.productImage.url based on your actual config
      const imgBaseUrl = SummaryApi.productImage?.url || "http://localhost:8080/images/";

      setProduct({
        product_name: productData.product_name || "",
        product_description: productData.product_description || "",
        price: productData.price || "",
        // Keep original string URL for display if no new file selected
        product_img1: productData.product_img1 ? imgBaseUrl + productData.product_img1 : "",
        product_img2: productData.product_img2 ? imgBaseUrl + productData.product_img2 : "",
        product_code: productData.product_code || "",
        sub_title: productData.sub_title || "",
        specifications: productData.specifications || "",
        discount: productData.discount || "",
        after_discount_price: productData.after_discount_price || productData.price || "",
        rating: productData.rating || "5",
        is_active: productData.is_active?.toString() || "1",
      });

    } catch (error) {
      console.error(error);
      toast.error("Error connecting to server");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e, fieldName, previewName) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      
      // Update file object in state
      setProduct({ ...product, [fieldName]: file });
      
      // Create local preview URL
      const objectUrl = URL.createObjectURL(file);
      setPreviews({ ...previews, [previewName]: objectUrl });
    }
  };

  const calculateAfterDiscount = () => {
    if (product.price) {
      const price = parseFloat(product.price);
      const discount = parseFloat(product.discount) || 0;
      const afterDiscount = price - (price * discount / 100);
      setProduct({ ...product, after_discount_price: afterDiscount.toFixed(2) });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!product.product_name.trim()) return toast.error('Product name is required');
    if (!product.price) return toast.error('Price is required');

    setUpdating(true);
    
    try {
      const formData = new FormData();
      formData.append('product_id', id);
      formData.append('product_name', product.product_name);
      formData.append('product_description', product.product_description);
      formData.append('price', product.price);
      formData.append('product_code', product.product_code || '');
      formData.append('sub_title', product.sub_title || '');
      formData.append('specifications', product.specifications || '');
      formData.append('discount', product.discount || '');
      formData.append('after_discount_price', product.after_discount_price || product.price);
      formData.append('rating', product.rating);
      formData.append('is_active', product.is_active);
      
      // Only append if it's a File object (not the existing URL string)
      if (product.product_img1 instanceof File) {
        formData.append('product_img1', product.product_img1);
      }
      if (product.product_img2 instanceof File) {
        formData.append('product_img2', product.product_img2);
      }
      
      const response = await fetch(SummaryApi.product.update.url, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${SummaryApi.bearerToken}`
        }
      });

      const data = await response.json();
      
      if (data.success || data.status) {
        toast.success('Product updated successfully!');
        navigate('/admin-panel/products');
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch (error) {
      console.error(error);
      toast.error('Server error occurred');
    } finally {
      setUpdating(false);
    }
  };

  // Helper to get image source (Preview -> New File -> Existing URL -> Placeholder)
  const getImageSource = (previewKey, productKey) => {
    if (previews[previewKey]) return previews[previewKey];
    if (typeof product[productKey] === 'string' && product[productKey]) return product[productKey];
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 md:p-8">
      <form onSubmit={handleSubmit} className="max-w-7xl mx-auto">
        
        {/* Top Header & Actions */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <button
              type="button"
              onClick={() => navigate('/admin-panel/products')}
              className="flex items-center text-gray-500 hover:text-gray-800 transition-colors text-sm font-medium mb-2"
            >
              <FaArrowLeft className="mr-2" /> Back to Products
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Update Product</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin-panel/products')}
              className="px-6 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updating}
              className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {updating ? (
                <>Processing...</>
              ) : (
                <><FaSave className="mr-2" /> Save Changes</>
              )}
            </button>
          </div>
        </div>

        {/* 2-Column Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* --- LEFT COLUMN (Content) --- */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* 1. Basic Information */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center pb-3 border-b border-gray-100">
                <FaBox className="text-blue-500 mr-2" /> Basic Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Product Name</label>
                  <input
                    type="text"
                    value={product.product_name}
                    onChange={(e) => setProduct({...product, product_name: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border-transparent focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                    placeholder="e.g. Premium Leather Jacket"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Sub Title <span className="text-gray-400 font-normal">(Optional)</span></label>
                  <input
                    type="text"
                    value={product.sub_title}
                    onChange={(e) => setProduct({...product, sub_title: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border-transparent focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                    placeholder="e.g. Limited Edition"
                  />
                </div>

              
              </div>
            </div>

            {/* 2. Pricing Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center pb-3 border-b border-gray-100">
                <FaTag className="text-green-500 mr-2" /> Pricing
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Base Price (₹)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-3.5 text-gray-400"><FaRupeeSign /></span>
                    <input
                      type="number"
                      value={product.price}
                      onChange={(e) => setProduct({...product, price: e.target.value})}
                      onBlur={calculateAfterDiscount}
                      className="w-full pl-8 pr-4 py-3 rounded-lg bg-gray-50 border-transparent focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none font-medium text-gray-800"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Discount (%)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-3.5 text-gray-400"><FaPercentage /></span>
                    <input
                      type="number"
                      value={product.discount}
                      onChange={(e) => setProduct({...product, discount: e.target.value})}
                      onBlur={calculateAfterDiscount}
                      className="w-full pl-8 pr-4 py-3 rounded-lg bg-gray-50 border-transparent focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                  <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">Final Price</p>
                  <p className="text-2xl font-bold text-blue-700">₹ {product.after_discount_price || 0}</p>
                </div>
              </div>
            </div>

            {/* 3. Details & Specs */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center pb-3 border-b border-gray-100">
                <FaAlignLeft className="text-purple-500 mr-2" /> Details
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Description</label>
                  <textarea
                    value={product.product_description}
                    onChange={(e) => setProduct({...product, product_description: e.target.value})}
                    rows="5"
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border-transparent focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none resize-y"
                    placeholder="Describe your product in detail..."
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Specifications</label>
                  <textarea
                    value={product.specifications}
                    onChange={(e) => setProduct({...product, specifications: e.target.value})}
                    rows="3"
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border-transparent focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                    placeholder="Size: XL, Color: Black, Material: Cotton..."
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          {/* --- RIGHT COLUMN (Meta) --- */}
          <div className="space-y-8">
            
            {/* 1. Visibility Status */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-bold text-gray-800 mb-4">Product Visibility</h3>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex flex-col">
                  <span className={`font-bold ${product.is_active === '1' ? 'text-green-600' : 'text-gray-500'}`}>
                    {product.is_active === '1' ? 'Active' : 'Hidden'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {product.is_active === '1' ? 'Visible on store' : 'Not visible'}
                  </span>
                </div>
                
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer"
                    checked={product.is_active === '1'}
                    onChange={(e) => setProduct({...product, is_active: e.target.checked ? '1' : '0'})}
                  />
                  <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>
            </div>

            {/* 2. Media Upload */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                <FaImages className="mr-2 text-orange-500" /> Media
              </h3>

              {/* Image 1 */}
              <div className="mb-6">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Primary Image</label>
                <div className="relative group">
                  <div className="w-full h-56 rounded-xl overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center relative">
                    {getImageSource('img1', 'product_img1') ? (
                      <img 
                        src={getImageSource('img1', 'product_img1')} 
                        alt="Primary" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center text-gray-400">
                        <FaCloudUploadAlt className="mx-auto text-4xl mb-2" />
                        <span className="text-sm">Upload Image</span>
                      </div>
                    )}
                    
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                      <p className="text-white font-medium bg-black bg-opacity-50 px-3 py-1 rounded-full text-sm">Change Image</p>
                    </div>
                    
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'product_img1', 'img1')}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              {/* Image 2 */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Secondary Image</label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 relative group flex-shrink-0">
                    {getImageSource('img2', 'product_img2') ? (
                      <img 
                        src={getImageSource('img2', 'product_img2')} 
                        alt="Secondary" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <FaCloudUploadAlt className="text-2xl" />
                      </div>
                    )}
                     <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'product_img2', 'img2')}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      title="Upload Secondary Image"
                    />
                  </div>
                  <div className="text-sm text-gray-500">
                    <p>Click the box to upload an alternative view.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Rating */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                <FaStar className="mr-2 text-yellow-400" /> Rating Control
              </h3>
              
              <div className="flex items-center gap-4 mb-2">
                <input
                  type="range"
                  min="0"
                  max="5"
                  step="0.5"
                  value={product.rating}
                  onChange={(e) => setProduct({...product, rating: e.target.value})}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <span className="font-bold text-gray-700 w-8">{product.rating}</span>
              </div>
              <div className="flex text-yellow-400 text-sm">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className={i < Math.floor(product.rating) ? "text-yellow-400" : "text-gray-300"} />
                ))}
              </div>
            </div>

          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductUpdate;